import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/contexts/AuthContext';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';

// Above this many accessible views the switcher shows a search box + "Shared with me" grouping.
const FLAT_MAX = 6;

interface ViewSwitcherProps {
  viewType: 'timeline' | 'list' | 'kanban' | 'calendar';
  to: string;
  label: string;
  active: boolean;
  navLinkClass: (active: boolean) => string;
}

// Character cap for the view name shown under the Timeline/List label
// (e.g. "Pymts FY26 Q4.."). Full title is always available on hover.
const HEADER_TITLE_MAX = 14;
function truncateChars(s: string, n = HEADER_TITLE_MAX): string {
  return s.length > n ? `${s.slice(0, n).trimEnd()}..` : s;
}

// A dropdown row: title (truncated with an immediate tooltip when clipped), an inline ★ when
// it's the user's favorite, and a small cropped owner name underneath for shared views.
function ViewMenuRow({
  title,
  ownerName,
  isFavorite,
  selected,
  onClick,
}: {
  title: string;
  ownerName?: string;
  isFavorite?: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [show, setShow] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const handleEnter = () => {
    const el = spanRef.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth);
    setShow(true);
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      className={`relative w-full flex items-center justify-between gap-2 px-4 py-1.5 text-sm text-left transition-colors ${
        selected ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span ref={spanRef} className="truncate">{title}</span>
          {isFavorite && <StarSolid className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
        </span>
        {ownerName && (
          <span className="block text-[11px] text-gray-400 truncate leading-tight">{ownerName}</span>
        )}
      </span>
      {selected && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
      {show && truncated && (
        <div className="absolute left-4 top-full mt-0.5 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 max-w-xs truncate">
          {title}
        </div>
      )}
    </button>
  );
}

// Small uppercase section header inside the dropdown
function MenuSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </div>
  );
}

/**
 * Renders the Timeline/List nav link plus a caret dropdown to switch between the
 * default view and the user's accessible named saved views of that type. The
 * caret only appears when at least one saved view of this type is accessible and
 * the Saved Views lab is enabled.
 */
export function ViewSwitcher({ viewType, to, label, active, navLinkClass }: ViewSwitcherProps) {
  const navigate = useNavigate();
  const { accessibleViews, activeSavedView, setActiveSavedView } = useAppStore();
  const setSkipFavoriteLoad = useAppStore((s) => s.setSkipFavoriteLoad);
  const { user } = useAuth();
  const labs = useLabsFeatures();
  const [open, setOpen] = useState(false);

  // Persist the user's explicit view choice per type so a page refresh keeps them on it
  // (falls back to the pinned favorite only when no explicit choice has been made).
  const persistActiveView = (val: string | null) => {
    if (!user?.id) return;
    const k = `cp_active_view_${viewType}_${user.id}`;
    if (val === null) localStorage.removeItem(k);
    else localStorage.setItem(k, val);
  };

  // Clicking the plain nav label clears the remembered choice → the favorite applies again.
  const onLabelClick = () => {
    setSkipFavoriteLoad(false);
    persistActiveView(null);
  };
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const savedViewsOn = labs.isEnabled('savedViews');
  const views = useMemo(
    () =>
      savedViewsOn
        ? accessibleViews
            .filter((v) => v.viewType === viewType)
            .slice()
            .sort((a, b) => a.title.localeCompare(b.title))
        : [],
    [savedViewsOn, accessibleViews, viewType],
  );

  const grouped = views.length > FLAT_MAX;
  const q = query.trim().toLowerCase();
  const matches = (v: (typeof views)[number]) =>
    !q || v.title.toLowerCase().includes(q) || (v.owner?.name?.toLowerCase().includes(q) ?? false);
  const owned = views.filter((v) => v.ownerId === user?.id && matches(v));
  const shared = views.filter((v) => v.ownerId !== user?.id && matches(v));

  // Focus the search box when the menu opens in grouped mode
  useEffect(() => {
    if (open && grouped) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open, grouped]);

  const selectDefault = () => {
    setOpen(false);
    setQuery('');
    // Explicit choice — don't let the Navbar's landing effect override it with the favorite,
    // and remember it so a page refresh keeps the user on the Default view.
    setSkipFavoriteLoad(true);
    persistActiveView('__default__');
    setActiveSavedView(null);
    navigate(to);
  };

  const selectView = (id: string) => {
    const view = accessibleViews.find((v) => v.id === id);
    if (!view) return;
    setOpen(false);
    setQuery('');
    // Explicit choice — don't let the Navbar's landing effect override it with the favorite,
    // and remember it so a page refresh keeps the user on this view.
    setSkipFavoriteLoad(true);
    persistActiveView(view.id);
    setActiveSavedView(view);
    navigate(to);
  };

  // No accessible saved views → plain link
  if (views.length === 0) {
    return (
      <Link to={to} className={navLinkClass(active)} onClick={onLabelClick}>
        {label}
      </Link>
    );
  }

  const activeForType = active && activeSavedView?.viewType === viewType ? activeSavedView : null;

  return (
    <div className="relative flex items-center" ref={ref}>
      <Link to={to} className={navLinkClass(active)} onClick={onLabelClick}>
        {label}
      </Link>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`-ml-1 p-1 rounded transition-colors ${active ? 'text-blue-600' : 'text-gray-500 hover:text-gray-900'}`}
        title={`Switch ${label} view`}
        aria-label={`Switch ${label} view`}
      >
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Active view name — floated below the label so it never shifts the row or the caret.
          Immediate custom tooltip (no browser delay) with the full title when truncated. */}
      {activeForType && (
        <div className="absolute left-3 top-full -mt-1.5 group/vn">
          <span className="block text-[11px] leading-none font-normal text-gray-500 whitespace-nowrap cursor-default">
            {truncateChars(activeForType.title)}
          </span>
          {activeForType.title.length > HEADER_TITLE_MAX && (
            <div className="absolute left-0 top-full mt-1 hidden group-hover/vn:block px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
              {activeForType.title}
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {grouped && (
            <div className="p-2 border-b border-gray-100">
              <div className="flex items-center gap-2 px-2 py-1 border border-gray-200 rounded text-sm focus-within:border-blue-500">
                <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search views…"
                  className="flex-1 outline-none text-sm bg-transparent"
                />
              </div>
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden py-1">
            {!q && (
              <>
                <button
                  onClick={selectDefault}
                  className={`w-full flex items-center justify-between px-4 py-1.5 text-sm transition-colors ${
                    active && !activeForType ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>Default {label}</span>
                  {active && !activeForType && <CheckIcon className="w-4 h-4" />}
                </button>
                <div className="my-1 border-t border-gray-100" />
              </>
            )}

            {owned.length > 0 && (
              <>
                <MenuSectionHeader>My Views</MenuSectionHeader>
                {owned.map((v) => (
                  <ViewMenuRow
                    key={v.id}
                    title={v.title}
                    isFavorite={v.isFavorite}
                    selected={activeForType?.id === v.id}
                    onClick={() => selectView(v.id)}
                  />
                ))}
              </>
            )}

            {shared.length > 0 &&
              (grouped ? (
                <>
                  <MenuSectionHeader>Shared with me</MenuSectionHeader>
                  {shared.map((v) => (
                    <ViewMenuRow
                      key={v.id}
                      title={v.title}
                      ownerName={v.owner?.name}
                      isFavorite={v.isFavorite}
                      selected={activeForType?.id === v.id}
                      onClick={() => selectView(v.id)}
                    />
                  ))}
                </>
              ) : (
                <>
                  {owned.length > 0 && <div className="my-1 border-t border-gray-100" />}
                  {shared.map((v) => (
                    <ViewMenuRow
                      key={v.id}
                      title={v.title}
                      ownerName={v.owner?.name}
                      isFavorite={v.isFavorite}
                      selected={activeForType?.id === v.id}
                      onClick={() => selectView(v.id)}
                    />
                  ))}
                </>
              ))}

            {q && owned.length === 0 && shared.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-400 text-center">No views match.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
